import { localize } from "client_legacy/ndaw/culture";
import { getEffectiveEmailClient, EmailClient } from "client_legacy/Email/emailClient";
import { createUrlRelativeToEnvironment, navigateTo } from "client_legacy/ndaw/navigator";
import { Highlight } from "client/Components";
import { isNotNilOrWhiteSpace } from "client/Misc/utils";

interface ContactForDisplayNameComputation {
	firstName?: string | nil;
	middleName?: string | nil;
	lastName?: string | nil;
	company?: string | nil;
	displayName?: string | nil;
	emailAddress?: string | nil;
	phoneNumber?: string | nil;
}

export type ComputedDisplayNameResult = {
	computedDisplayName: string;
	initials: string;
	highlight: Highlight | undefined;
};

function getComputedDisplayName(
	contact: ContactForDisplayNameComputation,
	fieldHighlights: Map<string, Highlight> = new Map<string, Highlight>(),
	shouldPreferDisplayName: boolean = false
): ComputedDisplayNameResult {
	const computers: IDisplayNameComputer[] = shouldPreferDisplayName
		? [
			new GenericDisplayNameComputer("displayName"),
			new DisplayNameFromNamesComputer(),
			new GenericDisplayNameComputer("company"),
			new GenericDisplayNameComputer("emailAddress"),
			new PhoneNumberDisplayNameComputer()
		]
		: [
			new DisplayNameFromNamesComputer(),
			new GenericDisplayNameComputer("company"),
			new GenericDisplayNameComputer("displayName"),
			new GenericDisplayNameComputer("emailAddress"),
			new PhoneNumberDisplayNameComputer()
		];

	for (const computer of computers) {
		const result: DisplayNameResult = computer.compute(contact, fieldHighlights);
		if (result.found) {
			const {
				computedDisplayName,
				initials,
				highlight
			} = result;

			return {
				computedDisplayName,
				initials,
				highlight
			};
		}
	}

	return {
		computedDisplayName: localize("_NoNameAvailable"),
		initials: "",
		highlight: undefined
	};
}

function getContactPhotoUrl(emailAddress: string): string {
	return createUrlRelativeToEnvironment(`/Email/Contacts/GetContactPhoto?emailAddress=${encodeURIComponent(emailAddress)}`);
}

type SuccessDisplayNameResult = {
	found: true;
	computedDisplayName: string;
	initials: string;
	highlight: Highlight | undefined;
};

type FailedDisplayNameResult = {
	found: false;
};

type DisplayNameResult = SuccessDisplayNameResult | FailedDisplayNameResult;

interface IDisplayNameComputer {
	compute(
		contact: ContactForDisplayNameComputation,
		fieldHighlights: Map<string, Highlight>
	): DisplayNameResult;
}

class GenericDisplayNameComputer implements IDisplayNameComputer {

	private readonly _propertyName: keyof ContactForDisplayNameComputation;

	constructor(propertyName: keyof ContactForDisplayNameComputation) {
		this._propertyName = propertyName;
	}

	public compute(
		contact: ContactForDisplayNameComputation,
		fieldHighlights: Map<string, Highlight>
	): DisplayNameResult {
		const value: string | nil = contact[this._propertyName]
			?.trim();

		if (!value) {
			return { found: false };
		}

		return {
			found: true,
			computedDisplayName: value,
			initials: this.getInitials(value),
			highlight: fieldHighlights.get(this._propertyName)
		};
	}

	protected getInitials(value: string): string {
		const segments: string[] = value.trim()
			.split(" ")
			.filter(segment => segment.length > 0);

		if (segments.length >= 2) {
			return segments[0][0] + segments[1][0];
		} else if (segments.length === 1) {
			return segments[0][0];
		} else {
			return "";
		}
	}
}

class PhoneNumberDisplayNameComputer extends GenericDisplayNameComputer {
	constructor() {
		super("phoneNumber");
	}

	protected override getInitials(_value: string): string {
		return "";
	}
}

class DisplayNameFromNamesComputer implements IDisplayNameComputer {
	public compute(
		contact: ContactForDisplayNameComputation,
		fieldHighlights: Map<string, Highlight>
	): DisplayNameResult {
		const { firstName, middleName, lastName } = contact;
		const segments: { value: string; availableForInitial: boolean; highlight: Highlight | undefined; }[] = [];

		if (isNotNilOrWhiteSpace(firstName)) {
			segments.push({
				value: firstName,
				availableForInitial: true,
				highlight: fieldHighlights.get("firstName")
			});
		}
		if (isNotNilOrWhiteSpace(middleName)) {
			segments.push({
				value: middleName,
				availableForInitial: false,
				highlight: fieldHighlights.get("middleName")
			});
		}
		if (isNotNilOrWhiteSpace(lastName)) {
			segments.push({
				value: lastName,
				availableForInitial: true,
				highlight: fieldHighlights.get("lastName")
			});
		}

		if (segments.length === 0) {
			return { found: false };
		}

		return segments.reduce<SuccessDisplayNameResult>(
			(previousValue, currentValue, currentIndex): SuccessDisplayNameResult => {
				const result: SuccessDisplayNameResult = {
					...previousValue,
					computedDisplayName: previousValue.computedDisplayName
						+ (currentIndex > 0 ? " " : "")
						+ currentValue.value,
					initials: previousValue.initials
						+ (currentValue.availableForInitial ? currentValue.value.trim()[0] : "")
				};

				if (!result.highlight && currentValue.highlight) {
					const [start, length] = currentValue.highlight;
					result.highlight = [
						(result.computedDisplayName.length - currentValue.value.length) + start,
						length
					];
				}

				return result;
			},
			{
				found: true,
				computedDisplayName: "",
				initials: "",
				highlight: undefined
			}
		);
	}
}

function sendEmail(emailAddress: string) {
	const emailClient: EmailClient = getEffectiveEmailClient();
	if (!emailClient.supportsEmailCompose) {
		throw new Error("Current email client does not support composing emails");
	}
	emailClient.composeEmail({to: emailAddress});
}

function call(phoneNumber: string) {
	navigateTo(`tel:${phoneNumber}`);
}

export {
	getComputedDisplayName,
	getContactPhotoUrl,
	sendEmail,
	call
};
